import { Button } from "@pepperstone/pepper-ui-kit";
import { type PropsWithChildren, type ReactNode, useState } from "react";
import {
  Accordion,
  AccordionHeaderWithIcon,
} from "@/components/general/accordion";
import { BaseModal } from "@/components/general/base-modal/BaseModal";
import { OrderedList } from "@/components/general/ordered-list/OrderedList";
import styles from "./InstructionsModal.module.css";
import type {
  GeneralInstructionsModalProps,
  InstructionsModalProps,
} from "./types";

export const InstructionsModal = ({
  isOpen,
  onClose,
  title,
  instructions,
  asAccordions,
  fixedHeight,
  "data-testid": testId,
  defaultInstructionIndex,
}: InstructionsModalProps) => {
  const defaultExpandedInstructionSectionIdx =
    typeof defaultInstructionIndex === "number" ? defaultInstructionIndex : 0;

  return (
    <BaseModal
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      data-testid={testId}
      testId={testId}
      height={fixedHeight}
      hideCloseIconOnMobile={!asAccordions}
    >
      {asAccordions ? (
        instructions.map((section, index) => (
          <Accordion
            isExpanded={defaultExpandedInstructionSectionIdx === index}
            id={section.ariaControls}
            ariaControls={section.ariaControls}
            key={section.ariaControls}
          >
            <Accordion.Header>
              <AccordionHeaderWithIcon>
                <p>{section.headerTitle}</p>
              </AccordionHeaderWithIcon>
            </Accordion.Header>
            <Accordion.Body>
              <div className={styles.instructionsModalItems}>
                <OrderedList items={section.items} />
              </div>
            </Accordion.Body>
          </Accordion>
        ))
      ) : (
        <OrderedList items={instructions} />
      )}
    </BaseModal>
  );
};

/**
 * Internal hook to manage modal state
 */
const useInstructionsModal = (props: GeneralInstructionsModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const modal = (
    <InstructionsModal
      {...props}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    />
  );

  return { isOpen, setIsOpen, modal };
};

/**
 * Outlined Button trigger
 */
InstructionsModal.Button = function InstructionsModalButton({
  children,
  ...props
}: GeneralInstructionsModalProps & PropsWithChildren) {
  const { setIsOpen, modal } = useInstructionsModal(props);

  return (
    <>
      <Button
        variant="ps-btn-outline"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {children}
      </Button>
      {modal}
    </>
  );
};

/**
 * Link-style trigger
 */
InstructionsModal.Link = function InstructionsModalLink({
  children,
  ...props
}: GeneralInstructionsModalProps & {
  children?: ReactNode;
}) {
  const { setIsOpen, modal } = useInstructionsModal(props);

  return (
    <>
      <button
        type="button"
        className={styles["instructions-link"]}
        onClick={() => setIsOpen(true)}
      >
        {children ?? "Instructions"}
      </button>
      {modal}
    </>
  );
};
